import { useEffect, useId, useImperativeHandle, useLayoutEffect, useRef, type CSSProperties } from 'react';
import { useReducedMotion } from '@mantine/hooks';
import type { BotAvatarProps } from './BotAvatar.types';
import { droidShape } from './droidShape';
import { Sim, restPose } from './Sim';
import { draw, OVERSCAN, RISE, type DrawConfig } from './draw';
import { pointer, subscribe } from './ticker';

/* A 0–1 seed from the React id, so two avatars side by side never blink in step. */
function hashSeed(id: string): number {
    let h = 2166136261;
    for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
    return ((h >>> 0) % 1000) / 1000;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Number.isFinite(v) ? v : 1));

const pathCache = new Map<string, Path2D>();
function bodyPath(d: string): Path2D {
    let p = pathCache.get(d);
    if (!p) {
        p = new Path2D(d);
        pathCache.set(d, p);
    }
    return p;
}

const devicePixels = () => Math.min(2, (typeof devicePixelRatio === 'number' && devicePixelRatio) || 1);

/* the layout size before the canvas is measured */
const FALLBACK_PX = 64;
/* how far the pointer's pull reaches, in head widths */
const REACH = 3;

export function BotAvatar({
    ref,
    state = 'default',
    size,
    speed = 1,
    paused = false,
    followPointer = true,
    turn = 1,
    jumpSpin = 1,
}: BotAvatarProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const seed = hashSeed(useId());
    const reducedMotion = useReducedMotion(undefined, { getInitialValueInEffect: false });
    const frozen = paused || reducedMotion || !(speed > 0);

    /* the sim lives across renders; props reach it through refs */
    const sim = useRef<Sim | null>(null);
    const cfg = useRef<DrawConfig | null>(null);
    const cssSize = useRef(0);
    const speedRef = useRef(speed);
    speedRef.current = speed;
    const frozenRef = useRef(frozen);
    frozenRef.current = frozen;
    const followRef = useRef(followPointer);
    followRef.current = followPointer;

    cfg.current =
        typeof Path2D === 'undefined'
            ? null
            : {
                  path: bodyPath(droidShape.outline),
                  parts: bodyPath(droidShape.parts),
                  key: droidShape.key,
                  faceX: droidShape.faceX,
                  faceY: droidShape.faceY,
                  faceScale: droidShape.faceScale,
                  still: frozen,
              };

    useImperativeHandle(
        ref,
        () => ({
            jump: () => {
                if (!frozenRef.current) sim.current?.poke();
            },
        }),
        [],
    );

    /* paint the current pose (the still pose of the state under reduced
       motion), sizing the backing store to the element */
    const paint = () => {
        const canvas = canvasRef.current;
        const c = cfg.current;
        if (!canvas || !c) return;
        /* a hidden ancestor measures 0: keep the last size rather than
           wiping the backing store */
        const px = canvas.clientWidth / OVERSCAN || cssSize.current || FALLBACK_PX;
        const dpr = devicePixels();
        const want = Math.round(px * OVERSCAN * dpr);
        if (canvas.width !== want || canvas.height !== want) {
            canvas.width = want;
            canvas.height = want;
        }
        cssSize.current = px;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const pose = reducedMotion || !sim.current ? restPose(state) : sim.current.pose;
        draw(ctx, px, dpr, pose, c);
    };

    /* first paint before the browser shows the frame */
    useLayoutEffect(() => {
        if (!sim.current) sim.current = new Sim(seed, state);
        else sim.current.setState(state);
        sim.current.setTurn(clamp(turn, 0, 2));
        sim.current.setJumpSpin(Math.max(0, Math.round(jumpSpin)));
        paint();
    });

    /* the loop: only while on screen and animated (the ticker itself rests
       in a hidden tab); the frame reads the current props through the refs */
    useEffect(() => {
        if (frozen) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        let unsub: (() => void) | null = null;
        const tick = (dt: number) => {
            const s = sim.current;
            if (!s) return;
            if (followRef.current && !Number.isNaN(pointer.x)) {
                const r = canvas.getBoundingClientRect();
                const box = r.width / OVERSCAN || 1;
                const dx = (pointer.x - (r.left + r.width / 2)) / box;
                const dy = (pointer.y - (r.top + r.height / 2 + RISE * box)) / box;
                const d = Math.hypot(dx, dy);
                /* full pull up close, gone by REACH */
                const strength = d < 1 ? 1 : d > REACH ? 0 : 1 - (d - 1) / (REACH - 1);
                s.setPointer(dx / Math.max(1, d), dy / Math.max(1, d), strength);
            } else s.setPointer(0, 0, 0);
            s.update(dt * speedRef.current);
            paint();
        };
        const run = () => {
            if (!unsub) unsub = subscribe(tick);
        };
        const stop = () => {
            if (unsub) unsub();
            unsub = null;
        };
        let io: IntersectionObserver | null = null;
        if (typeof IntersectionObserver === 'function') {
            io = new IntersectionObserver((entries) => {
                if (entries[entries.length - 1]?.isIntersecting ?? true) run();
                else stop();
            });
            io.observe(canvas);
        } else run();
        return () => {
            stop();
            io?.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- кадр берёт пропсы из рефов, цикл перезапускает только frozen
    }, [frozen]);

    /* The canvas overscans its box (see draw.ts) and pulls itself back in
       with negative margins, so it lays out at `size` and still has room
       to hop and flip. */
    const pull = (k: number) => `calc(${size} * ${-k})`;
    const side = (OVERSCAN - 1) / 2;
    const css: CSSProperties = {
        display: 'inline-block',
        verticalAlign: 'middle',
        width: `calc(${size} * ${OVERSCAN})`,
        height: `calc(${size} * ${OVERSCAN})`,
        marginLeft: pull(side),
        marginRight: pull(side),
        marginTop: pull(side + RISE),
        marginBottom: pull(side - RISE),
        flex: 'none',
    };

    return <canvas ref={canvasRef} aria-hidden="true" style={css} />;
}
